import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { useDisplayMode } from './useDisplayMode';

const ThemeContext = createContext(null);
const validTheme = value => value === 'light' || value === 'dark';
const storageKey = userId => `trainnova.appearance.${userId}`;
function cachedTheme(user) {
  if (!user?.id) return 'dark';
  if (validTheme(user.appearance_theme)) return user.appearance_theme;
  try {
    const value = localStorage.getItem(storageKey(user.id));
    return validTheme(value) ? value : 'dark';
  } catch { return 'dark'; }
}
function remember(userId, theme) {
  if (!userId) return;
  try { localStorage.setItem(storageKey(userId), theme); } catch { /* Browser storage may be disabled. */ }
}
function applyTheme(theme) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
  document.querySelectorAll('meta[name="theme-color"]').forEach(meta => {
    meta.content = theme === 'light' ? '#f4f6f8' : '#0f1115';
  });
}
export function AppearanceProvider({ children }) {
  const { user, loading } = useAuth();
  const displayPreference = useDisplayMode(user?.id);
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme === 'light' ? 'light' : 'dark');
  const [saving, setSaving] = useState(false);
  const identity = useRef(user?.id);
  const request = useRef(0);
  const pending = useRef(false);
  const confirmed = useRef(theme);
  identity.current = user?.id;

  useLayoutEffect(() => {
    if (loading && !user?.id) return;
    request.current += 1;
    pending.current = false;
    setSaving(false);
    const next = cachedTheme(user);
    confirmed.current = next;
    setTheme(next);
    applyTheme(next);
    // A preference belongs to a person, never to the active team.
  }, [user?.id, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!pending.current && validTheme(user?.appearance_theme)) {
      confirmed.current = user.appearance_theme;
      setTheme(user.appearance_theme);
      remember(user.id, user.appearance_theme);
    }
  }, [user?.id, user?.appearance_theme]);

  useEffect(() => {
    if (loading || !user?.id) return;
    const userId = user.id;
    const generation = request.current;
    let cancelled = false;
    api.get('/auth/me/appearance').then(({ data }) => {
      if (cancelled || identity.current !== userId || generation !== request.current || !validTheme(data.theme)) return;
      confirmed.current = data.theme;
      setTheme(data.theme);
      remember(userId, data.theme);
    }).catch(() => { /* Keep the cached appearance when temporarily offline. */ });
    return () => { cancelled = true; };
  }, [user?.id, loading]);

  useLayoutEffect(() => { applyTheme(theme); }, [theme]);

  const changeTheme = async next => {
    if (!identity.current || !validTheme(next) || pending.current || next === theme) return;
    const userId = identity.current;
    const generation = ++request.current;
    const previous = confirmed.current;
    pending.current = true;
    setSaving(true);
    setTheme(next);
    try {
      await api.put('/auth/me/appearance', { theme: next });
      if (identity.current !== userId || generation !== request.current) return;
      confirmed.current = next;
      remember(userId, next);
      // Keep first-paint cache in sync without rewriting authentication state.
      try {
        const saved = JSON.parse(localStorage.getItem('user') || 'null');
        if (saved?.id === userId) localStorage.setItem('user', JSON.stringify({ ...saved, appearance_theme: next }));
      } catch { /* The server remains the source of truth. */ }
    } catch (error) {
      if (identity.current === userId && generation === request.current) {
        setTheme(previous);
        throw error;
      }
    } finally {
      if (identity.current === userId && generation === request.current) {
        pending.current = false;
        setSaving(false);
      }
    }
  };
  return <ThemeContext.Provider value={{ theme, saving, changeTheme, ...displayPreference }}>{children}</ThemeContext.Provider>;
}
export const useAppearance = () => useContext(ThemeContext);
